import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import MessagingRoutesPanel from "./settings/MessagingRoutesPanel";
import MessageTemplatesPanel from "./settings/MessageTemplatesPanel";
import { TwilioCallbackSection } from "./settings/TwilioCallbackSection";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Trash2, FlaskConical, Send, Eye, Zap } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { InboundNotifyPanel } from "@/components/settings/InboundNotifyPanel";
import { TwilioPreflightPanel, LiveAllowanceControl } from "@/components/settings/TwilioPreflightPanel";

export default function Settings() {
    const { user } = useAuth();
    const [tab, setTab] = useState("providers");
    const [s, setS] = useState(null);
    const [users, setUsers] = useState([]);
    const [emailTemplates, setEmailTemplates] = useState([]);
    const [newUser, setNewUser] = useState({ email: "", name: "", role: "staff", password: "" });

    // Test-send local state
    const [testSms, setTestSms] = useState({ phone: "", template_id: "", body: "", variables: '{"first_name":"Alex"}' });
    const [testSmsResult, setTestSmsResult] = useState(null);
    const [testSmsBusy, setTestSmsBusy] = useState(false);
    const [testEmail, setTestEmail] = useState({ email: "", template_id: "", subject: "", variables: '{"first_name":"Alex"}' });
    const [testEmailResult, setTestEmailResult] = useState(null);
    const [testEmailBusy, setTestEmailBusy] = useState(false);

    const [loadError, setLoadError] = useState(null);
    // SMS routes used in the Voicemail panel dropdown. Loaded once.
    const [smsRoutes, setSmsRoutes] = useState([]);
    // Per-endpoint diagnostics so failures are never silent. Shape:
    //   { endpoint: "/settings", method: "GET", ok: false|true,
    //     status: 401|500|0|..., message: "...", attempted_at: iso }
    const [diagnostics, setDiagnostics] = useState([]);

    const _formatDiag = (label, res) => {
        if (res.status === "fulfilled") {
            return { endpoint: label, method: "GET", ok: true,
                     status: res.value?.status || 200, message: null,
                     attempted_at: new Date().toISOString() };
        }
        const err = res.reason;
        return {
            endpoint: label, method: "GET", ok: false,
            status: err?.response?.status ?? 0,
            message: formatApiError(err),
            attempted_at: new Date().toISOString(),
        };
    };

    const load = async () => {
        setLoadError(null);
        // Each call is independent. If ONE fails, render the page with the
        // partials that succeeded (e.g., no email templates on a fresh DB
        // shouldn't trap the operator on an infinite "Loading…" spinner).
        const [a, b, c] = await Promise.allSettled([
            api.get("/settings"),
            api.get("/settings/users"),
            api.get("/message-templates", { params: { channel: "email", active: true } }),
        ]);
        setDiagnostics([
            _formatDiag("/api/settings", a),
            _formatDiag("/api/settings/users", b),
            _formatDiag("/api/message-templates?channel=email&active=true", c),
        ]);
        if (a.status === "fulfilled") {
            setS(a.value.data);
        } else {
            // Settings is the only call we MUST have — if it fails, the page can't render.
            setLoadError(formatApiError(a.reason));
            toast.error(`Settings: ${formatApiError(a.reason)}`);
            return;
        }
        setUsers(b.status === "fulfilled" ? b.value.data : []);
        setEmailTemplates(c.status === "fulfilled" ? c.value.data : []);
        if (b.status === "rejected") toast.error(`Users: ${formatApiError(b.reason)}`);
        if (c.status === "rejected") toast.error(`Email templates: ${formatApiError(c.reason)}`);
        // Routes — needed for the Voicemail panel dropdown. Non-blocking.
        try {
            const rr = await api.get("/messaging-routes");
            setSmsRoutes((rr.data || []).filter(r => r.channel === "sms" && r.active && r.approved));
        } catch (e) {
            // Settings still renders if this fails; the dropdown just stays empty.
            console.warn("failed to load messaging routes for voicemail panel", e);
        }
    };
    useEffect(() => { load(); }, []);

    const save = async () => {
        try {
            // P1A.1 — HTTPS-only guard for the callback URL when the app
            // is in live mode. In test mode we allow http:// so preview
            // devs on plain HTTP can still exercise the flow.
            const cbUrl = (s.twilio_status_callback_url || "").trim();
            if (cbUrl) {
                try {
                    const u = new URL(cbUrl);
                    if (!s.test_mode && u.protocol !== "https:") {
                        toast.error("Callback URL must use HTTPS in live mode.");
                        return;
                    }
                } catch {
                    toast.error("Callback URL is malformed.");
                    return;
                }
            }
            const payload = {
                twilio_account_sid: s.twilio_account_sid,
                twilio_messaging_service_sid: s.twilio_messaging_service_sid,
                twilio_from_number: s.twilio_from_number,
                twilio_status_callback_url: cbUrl,
                sendgrid_from_email: s.sendgrid_from_email,
                sendgrid_from_name: s.sendgrid_from_name,
                max_send_rate_per_hour: Number(s.max_send_rate_per_hour) || 1000,
                sms_templates: s.sms_templates,
                email_templates: s.email_templates,
                operating_hours: s.operating_hours,
                test_mode: s.test_mode,
                workflow_external_actions_enabled: !!s.workflow_external_actions_enabled,
                voicemail_enabled: !!s.voicemail_enabled,
                voicemail_staff_sender_name: s.voicemail_staff_sender_name || "",
                voicemail_callback_phone_e164: (s.voicemail_callback_phone_e164 || "").trim(),
                voicemail_route_id: s.voicemail_route_id || "",
                inbound_notify_enabled: !!s.inbound_notify_enabled,
                inbound_notify_phone_e164: (s.inbound_notify_phone_e164 || "").trim(),
            };
            if (s.twilio_auth_token && !s.twilio_auth_token.startsWith("•")) payload.twilio_auth_token = s.twilio_auth_token;
            if (s.sendgrid_api_key && !s.sendgrid_api_key.startsWith("•")) payload.sendgrid_api_key = s.sendgrid_api_key;
            await api.put("/settings", payload);
            toast.success("Settings saved");
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const parseVars = (raw) => {
        if (!raw?.trim()) return {};
        try { return JSON.parse(raw); }
        catch { throw new Error("Variables must be valid JSON, e.g. {\"first_name\":\"Alex\"}"); }
    };

    const previewSms = async () => {
        try {
            const variables = parseVars(testSms.variables);
            const { data } = await api.post("/settings/preview-sms", {
                phone: testSms.phone || "+15555550100",
                template_id: testSms.template_id || null,
                body: testSms.body || null,
                variables,
            });
            setTestSmsResult({ kind: "preview", ...data });
        } catch (e) {
            toast.error(e.message || formatApiError(e));
        }
    };

    const sendTestSms = async () => {
        if (!testSms.phone.trim()) return toast.error("Phone number is required");
        if (!testSms.template_id && !testSms.body.trim()) return toast.error("Pick a template or type a body");
        try {
            const variables = parseVars(testSms.variables);
            setTestSmsBusy(true);
            const { data } = await api.post("/settings/test-sms", {
                phone: testSms.phone,
                template_id: testSms.template_id || null,
                body: testSms.body || null,
                variables,
            });
            setTestSmsResult({ kind: "send", ...data });
            toast.success(data.mocked ? "Test SMS sent (mocked)" : "Test SMS sent (LIVE)");
        } catch (e) {
            toast.error(e.message || formatApiError(e));
        } finally {
            setTestSmsBusy(false);
        }
    };

    const sendTestEmail = async () => {
        if (!testEmail.email.trim()) return toast.error("Recipient email is required");
        try {
            const variables = parseVars(testEmail.variables);
            setTestEmailBusy(true);
            const { data } = await api.post("/settings/test-email", {
                email: testEmail.email,
                template_id: testEmail.template_id || null,
                subject: testEmail.subject || null,
                variables,
            });
            setTestEmailResult(data);
            toast.success(data.mocked ? "Test email sent (mocked)" : "Test email sent (LIVE)");
        } catch (e) {
            toast.error(e.message || formatApiError(e));
        } finally {
            setTestEmailBusy(false);
        }
    };

    const addUser = async () => {
        if (!newUser.email || !newUser.name) return;
        try {
            await api.post("/settings/users", newUser);
            setNewUser({ email: "", name: "", role: "staff", password: "" });
            toast.success("User created");
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const removeUser = async (id) => {
        if (!window.confirm("Delete user?")) return;
        await api.delete(`/settings/users/${id}`);
        load();
    };

    if (!s) {
        if (loadError) {
            const failed = diagnostics.find(d => !d.ok) || {};
            return (
                <div className="max-w-lg mx-auto mt-12 bg-white border border-rose-200 rounded-xl p-6" data-testid="settings-load-error">
                    <div className="text-rose-700 font-semibold mb-1">Couldn't load Settings</div>
                    <div className="text-sm text-gray-700 mb-3">{loadError}</div>
                    <div className="bg-rose-50 border border-rose-100 rounded-md p-3 text-xs font-mono text-gray-800 space-y-1" data-testid="settings-diag-block">
                        {diagnostics.map((d, i) => (
                            <div key={i} className="flex items-start gap-2">
                                <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider ${d.ok ? "bg-emerald-100 text-emerald-700" : "bg-rose-200 text-rose-800"}`}>
                                    {d.ok ? "OK" : "FAIL"}
                                </span>
                                <span className="flex-1">
                                    <span className="text-gray-500">{d.method}</span> {d.endpoint}
                                    {" → "}
                                    <span className={d.ok ? "text-emerald-700" : "text-rose-700 font-medium"}>{d.status || "no-response"}</span>
                                    {!d.ok && d.message && <div className="text-rose-700 mt-0.5">{d.message}</div>}
                                </span>
                            </div>
                        ))}
                    </div>
                    <div className="flex items-center gap-2 mt-4">
                        <Button onClick={load} data-testid="settings-retry">Retry</Button>
                        <a href="/login" className="text-xs text-muted-foreground underline" data-testid="settings-relogin">Sign in again</a>
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-2">
                        If this keeps failing, share the failed endpoint + status code above with the platform team.
                    </p>
                </div>
            );
        }
        return <div className="text-sm text-muted-foreground">Loading…</div>;
    }

    return (
        <div className="space-y-6" data-testid="settings-page">
            <div>
                <h1 className="text-2xl md:text-3xl font-display font-semibold tracking-tight">Settings</h1>
                <p className="text-sm text-muted-foreground mt-1">Provider credentials, templates and users.</p>
            </div>

            {diagnostics.some(d => !d.ok) && (
                <div className="rounded-lg border border-amber-200 bg-amber-50/70 p-3 text-xs" data-testid="settings-partial-failures">
                    <div className="font-medium text-amber-800 mb-1.5">Some sub-requests failed — the page is rendering with whatever loaded.</div>
                    <div className="space-y-1 font-mono">
                        {diagnostics.filter(d => !d.ok).map((d, i) => (
                            <div key={i} className="text-amber-900">
                                <span className="text-amber-700">{d.method}</span> {d.endpoint} → <span className="font-medium">{d.status || "no-response"}</span>
                                {d.message && <span className="text-amber-800"> · {d.message}</span>}
                            </div>
                        ))}
                    </div>
                    <button onClick={load} className="mt-2 text-xs font-medium text-amber-900 underline" data-testid="settings-partial-retry">Retry</button>
                </div>
            )}

            <Tabs value={tab} onValueChange={setTab}>
                <TabsList>
                    <TabsTrigger value="providers" data-testid="tab-providers">Providers</TabsTrigger>
                    <TabsTrigger value="send-tests" data-testid="tab-send-tests">Send Tests</TabsTrigger>
                    <TabsTrigger value="templates" data-testid="tab-templates">Templates</TabsTrigger>
                    <TabsTrigger value="messaging-routes" data-testid="tab-messaging-routes">Twilio Messaging Routes</TabsTrigger>
                    <TabsTrigger value="hours" data-testid="tab-hours">Operating Hours</TabsTrigger>
                    <TabsTrigger value="users" data-testid="tab-users">Users</TabsTrigger>
                    <TabsTrigger value="limits" data-testid="tab-limits">Limits</TabsTrigger>
                </TabsList>

                <TabsContent value="providers" className="mt-4 space-y-4">
                    {/* Safety / Test Mode */}
                    <div
                        className={`rounded-xl border p-5 shadow-sm flex flex-col md:flex-row md:items-center gap-4 ${
                            s.test_mode ? "bg-amber-50/60 border-amber-200" : "bg-rose-50/60 border-rose-200"
                        }`}
                        data-testid="test-mode-card"
                    >
                        <div className="flex items-start gap-3 flex-1">
                            <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${s.test_mode ? "bg-amber-100 text-amber-700" : "bg-rose-100 text-rose-700"}`}>
                                {s.test_mode ? <FlaskConical className="h-5 w-5" /> : <Zap className="h-5 w-5" />}
                            </div>
                            <div>
                                <div className="text-sm font-medium text-gray-900">
                                    {s.test_mode ? "Test Mode is ON" : "LIVE Mode is ON"}
                                </div>
                                <p className="text-xs text-muted-foreground mt-1 max-w-xl leading-relaxed">
                                    {s.test_mode
                                        ? "All sends — campaign send loop, test SMS, test email and test batches — are mocked. No real messages leave the system."
                                        : "Real Twilio/SendGrid API calls will fire for campaign sends, test sends and test batches. Double-check credentials and recipients before launching a campaign."}
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <Label htmlFor="test-mode-switch" className="text-sm cursor-pointer">{s.test_mode ? "Switch to Live" : "Switch to Test"}</Label>
                            <Switch
                                id="test-mode-switch"
                                checked={!s.test_mode}
                                onCheckedChange={async (v) => {
                                    const next = !v;
                                    // optimistic
                                    setS({ ...s, test_mode: next });
                                    try {
                                        await api.put("/settings", { test_mode: next });
                                        toast.success(next ? "Test Mode enabled" : "LIVE Mode enabled");
                                    } catch (e) {
                                        setS({ ...s, test_mode: !next });
                                        toast.error(formatApiError(e));
                                    }
                                }}
                                data-testid="test-mode-toggle"
                            />
                        </div>
                    </div>

                    {/* W2C-2 — Workflow External Actions master kill-switch */}
                    <div
                        className={`rounded-xl border p-5 shadow-sm flex flex-col md:flex-row md:items-center gap-4 ${
                            s.workflow_external_actions_enabled
                                ? "bg-rose-50/60 border-rose-200"
                                : "bg-slate-50/60 border-slate-200"
                        }`}
                        data-testid="workflow-external-card"
                    >
                        <div className="flex items-start gap-3 flex-1">
                            <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${s.workflow_external_actions_enabled ? "bg-rose-100 text-rose-700" : "bg-slate-200 text-slate-600"}`}>
                                <Zap className="h-5 w-5" />
                            </div>
                            <div>
                                <div className="text-sm font-medium text-gray-900">
                                    Workflow External Actions {s.workflow_external_actions_enabled ? "are ENABLED" : "are DISABLED"}
                                </div>
                                <p className="text-xs text-muted-foreground mt-1 max-w-xl leading-relaxed">
                                    {s.workflow_external_actions_enabled
                                        ? "Client workflow status changes will fire queued Send SMS / Send Email actions. Provider Test Mode above still controls whether those sends are LIVE or MOCKED."
                                        : "Send SMS / Send Email actions stay queued when a client changes workflow status. Flip this on when you're ready to have automation reach out to clients."}
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <Label htmlFor="wf-ext-switch" className="text-sm cursor-pointer">
                                {s.workflow_external_actions_enabled ? "Disable" : "Enable"}
                            </Label>
                            <Switch
                                id="wf-ext-switch"
                                checked={!!s.workflow_external_actions_enabled}
                                onCheckedChange={async (v) => {
                                    setS({ ...s, workflow_external_actions_enabled: v });
                                    try {
                                        await api.put("/settings", { workflow_external_actions_enabled: v });
                                        toast.success(v ? "Workflow external actions ENABLED" : "Workflow external actions DISABLED");
                                    } catch (e) {
                                        setS({ ...s, workflow_external_actions_enabled: !v });
                                        toast.error(formatApiError(e));
                                    }
                                }}
                                data-testid="workflow-external-toggle"
                            />
                        </div>
                    </div>

                    <div className="bg-white border border-border rounded-xl p-6 shadow-sm space-y-5 max-w-3xl">
                        <div>
                            <h2 className="text-base font-medium text-gray-900">Twilio (SMS) — Credentials</h2>
                            <p className="text-xs text-muted-foreground mt-0.5">Credentials are stored server-side and never sent back to the browser in plain text.</p>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <Label>Account SID</Label>
                                <Input value={s.twilio_account_sid || ""} onChange={e => setS({ ...s, twilio_account_sid: e.target.value })} data-testid="twilio-sid-input" placeholder="AC…" />
                            </div>
                            <div>
                                <Label>Auth Token</Label>
                                <Input type="password" value={s.twilio_auth_token || ""} onChange={e => setS({ ...s, twilio_auth_token: e.target.value })} data-testid="twilio-token-input" placeholder="••••••••" />
                            </div>
                            <div className="md:col-span-2">
                                <Label>Legacy Default From Number (fallback)</Label>
                                <Input value={s.twilio_from_number || ""} onChange={e => setS({ ...s, twilio_from_number: e.target.value })} data-testid="twilio-from-input" placeholder="+15555550100" />
                                <p className="text-[11px] text-muted-foreground mt-1">Used as a last-resort fallback when a route has no SID. Prefer Messaging Routes for all new automation.</p>
                            </div>
                        </div>

                        <div className="rounded-lg border border-indigo-200 bg-indigo-50/50 px-4 py-3 flex flex-wrap items-center gap-3" data-testid="messaging-routes-callout">
                            <div className="flex-1 min-w-[240px]">
                                <div className="text-sm font-medium text-gray-900">Messaging Service SIDs live under Messaging Routes</div>
                                <div className="text-xs text-muted-foreground">
                                    Each approved Twilio Messaging Service SID is now managed as its own route. Workflow automation SMS must use a template linked to an active route — the legacy Providers-level MSID is no longer editable here.
                                </div>
                            </div>
                            <Button size="sm" variant="outline" onClick={() => setTab("messaging-routes")} data-testid="open-messaging-routes-btn">
                                Open Messaging Routes →
                            </Button>
                        </div>

                        <TwilioCallbackSection s={s} setS={setS} />

                        {/* ── Voicemail → Auto Callback SMS ───────────────── */}
                        <div className="border-t border-border pt-5">
                            <h2 className="text-base font-medium text-gray-900">Voicemail → Auto Callback SMS</h2>
                            <p className="text-xs text-muted-foreground mt-1">
                                Incoming voicemail transcripts arrive via a Gmail Apps Script bridge
                                and trigger a short callback SMS through the chosen Twilio route.
                                See <code>/app/docs/voicemail_apps_script.md</code> for setup.
                            </p>
                        </div>
                        <div className="rounded-lg border border-border bg-background p-4 space-y-4" data-testid="voicemail-panel">
                            <div className="flex items-start justify-between gap-4">
                                <div>
                                    <div className="text-sm font-medium">Enable voicemail callbacks</div>
                                    <div className="text-[11px] text-muted-foreground max-w-md">
                                        Keep OFF until you've tested the Gmail connection and the
                                        route+callback phone are set. CaseFlow will store every
                                        voicemail even when disabled, but won't send any SMS.
                                    </div>
                                </div>
                                <Switch
                                    checked={!!s.voicemail_enabled}
                                    onCheckedChange={(v) => setS({ ...s, voicemail_enabled: v })}
                                    data-testid="voicemail-enabled-toggle"
                                />
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <Label>Staff sender name</Label>
                                    <Input
                                        value={s.voicemail_staff_sender_name || ""}
                                        onChange={e => setS({ ...s, voicemail_staff_sender_name: e.target.value })}
                                        placeholder="Aaron"
                                        data-testid="voicemail-staff-name-input"
                                    />
                                    <p className="text-[11px] text-muted-foreground mt-1">
                                        Appears in the SMS signature, e.g. "— Aaron, A+ Student Loans".
                                    </p>
                                </div>
                                <div>
                                    <Label>Callback phone (E.164)</Label>
                                    <Input
                                        value={s.voicemail_callback_phone_e164 || ""}
                                        onChange={e => setS({ ...s, voicemail_callback_phone_e164: e.target.value })}
                                        placeholder="+15551234567"
                                        data-testid="voicemail-callback-phone-input"
                                    />
                                    <p className="text-[11px] text-muted-foreground mt-1">
                                        Server will validate and store strictly as E.164.
                                    </p>
                                </div>
                                <div className="md:col-span-2">
                                    <Label>Twilio messaging route</Label>
                                    <select
                                        className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                                        value={s.voicemail_route_id || ""}
                                        onChange={e => setS({ ...s, voicemail_route_id: e.target.value })}
                                        data-testid="voicemail-route-select"
                                    >
                                        <option value="">— pick a route —</option>
                                        {smsRoutes.map(r => (
                                            <option key={r.id} value={r.id}>
                                                {r.name} {r.twilio_messaging_service_sid ? `(MG…${r.twilio_messaging_service_sid.slice(-4)})` : (r.twilio_from_number || "")}
                                            </option>
                                        ))}
                                    </select>
                                    <p className="text-[11px] text-muted-foreground mt-1">
                                        Reuses the existing CaseFlow route — the route's Messaging
                                        Service decides the sender number; no separate "from" is
                                        hardcoded for voicemail.
                                    </p>
                                </div>
                            </div>
                        </div>
                        <InboundNotifyPanel s={s} setS={setS} />
                        <TwilioPreflightPanel />
                        <LiveAllowanceControl />
                        <div className="border-t border-border pt-5">
                            <h2 className="text-base font-medium text-gray-900">SendGrid (Email)</h2>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <Label>API Key</Label>
                                <Input type="password" value={s.sendgrid_api_key || ""} onChange={e => setS({ ...s, sendgrid_api_key: e.target.value })} data-testid="sendgrid-key-input" placeholder="SG.•••" />
                            </div>
                            <div>
                                <Label>From Email</Label>
                                <Input value={s.sendgrid_from_email || ""} onChange={e => setS({ ...s, sendgrid_from_email: e.target.value })} data-testid="sendgrid-from-input" placeholder="ops@aplus.com" />
                            </div>
                            <div className="md:col-span-2">
                                <Label>From Name</Label>
                                <Input value={s.sendgrid_from_name || ""} onChange={e => setS({ ...s, sendgrid_from_name: e.target.value })} data-testid="sendgrid-from-name-input" placeholder="A+ Student Loans" />
                            </div>
                        </div>
                        <Button onClick={save} data-testid="save-settings-button">Save provider settings</Button>
                    </div>
                </TabsContent>

                <TabsContent value="send-tests" className="mt-4 space-y-6">
                    <div className="bg-white border border-border rounded-xl p-6 shadow-sm max-w-3xl space-y-4" data-testid="test-sms-panel">
                        <div className="flex items-center justify-between gap-3 flex-wrap">
                            <div>
                                <h2 className="text-base font-medium text-gray-900">Send Test SMS</h2>
                                <p className="text-xs text-muted-foreground mt-0.5">
                                    {s.test_mode ? "Mocked — Twilio is not called." : "LIVE — a real Twilio API call will be made."}
                                </p>
                            </div>
                            <span className={`text-[11px] uppercase tracking-wide px-2 py-0.5 rounded-md ${s.test_mode ? "bg-amber-100 text-amber-700" : "bg-rose-100 text-rose-700"}`}>
                                {s.test_mode ? "Test Mode" : "Live"}
                            </span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <Label>Recipient phone</Label>
                                <Input value={testSms.phone} onChange={e => setTestSms({ ...testSms, phone: e.target.value })} placeholder="+15555550100" data-testid="test-sms-phone" />
                            </div>
                            <div>
                                <Label>Template (optional)</Label>
                                <Select value={testSms.template_id || "none"} onValueChange={v => setTestSms({ ...testSms, template_id: v === "none" ? "" : v })}>
                                    <SelectTrigger data-testid="test-sms-template"><SelectValue placeholder="Use inline body" /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">— inline body —</SelectItem>
                                        {(s.sms_templates || []).map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="md:col-span-2">
                                <Label>Body {testSms.template_id && <span className="text-muted-foreground text-xs">(overridden by template)</span>}</Label>
                                <Input value={testSms.body} onChange={e => setTestSms({ ...testSms, body: e.target.value })} placeholder="Hi {{first_name}}, this is a test." data-testid="test-sms-body" />
                            </div>
                            <div className="md:col-span-2">
                                <Label>Variables (JSON)</Label>
                                <Input value={testSms.variables} onChange={e => setTestSms({ ...testSms, variables: e.target.value })} placeholder='{"first_name":"Alex"}' data-testid="test-sms-variables" className="font-mono text-xs" />
                            </div>
                        </div>
                        <div className="flex gap-2 flex-wrap">
                            <Button variant="outline" onClick={previewSms} data-testid="test-sms-preview">
                                <Eye className="h-4 w-4 mr-1" /> Preview payload
                            </Button>
                            <Button onClick={sendTestSms} disabled={testSmsBusy} data-testid="test-sms-send">
                                <Send className="h-4 w-4 mr-1" /> {testSmsBusy ? "Sending…" : "Send test SMS"}
                            </Button>
                        </div>
                        {testSmsResult && (
                            <div className="border border-border rounded-md bg-gray-50 p-3 text-xs font-mono text-gray-800 overflow-auto max-h-64" data-testid="test-sms-result">
                                <div className="mb-1 text-[11px] uppercase tracking-wider text-muted-foreground">
                                    {testSmsResult.kind === "preview" ? "Preview" : (testSmsResult.mocked ? "Result (mocked)" : "Result (LIVE)")}
                                </div>
                                <pre className="whitespace-pre-wrap break-words">{JSON.stringify(testSmsResult, null, 2)}</pre>
                            </div>
                        )}
                    </div>

                    <div className="bg-white border border-border rounded-xl p-6 shadow-sm max-w-3xl space-y-4" data-testid="test-email-panel">
                        <div className="flex items-center justify-between gap-3 flex-wrap">
                            <div>
                                <h2 className="text-base font-medium text-gray-900">Send Test Email</h2>
                                <p className="text-xs text-muted-foreground mt-0.5">
                                    {s.test_mode ? "Mocked — SendGrid is not called." : "LIVE — a real SendGrid API call will be made."}
                                </p>
                            </div>
                            <span className={`text-[11px] uppercase tracking-wide px-2 py-0.5 rounded-md ${s.test_mode ? "bg-amber-100 text-amber-700" : "bg-rose-100 text-rose-700"}`}>
                                {s.test_mode ? "Test Mode" : "Live"}
                            </span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <Label>Recipient email</Label>
                                <Input value={testEmail.email} onChange={e => setTestEmail({ ...testEmail, email: e.target.value })} placeholder="you@example.com" data-testid="test-email-to" />
                            </div>
                            <div>
                                <Label>Template (Email/SendGrid)</Label>
                                <Select value={testEmail.template_id || "none"} onValueChange={v => setTestEmail({ ...testEmail, template_id: v === "none" ? "" : v })}>
                                    <SelectTrigger data-testid="test-email-template"><SelectValue placeholder="No template (use subject)" /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">— no template —</SelectItem>
                                        {emailTemplates.map(t => (
                                            <SelectItem key={t.id} value={t.id}>
                                                <span className="font-mono text-[11px] text-muted-foreground mr-1">{t.template_key}</span>
                                                {t.template_name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="md:col-span-2">
                                <Label>Subject {testEmail.template_id && <span className="text-muted-foreground text-xs">(ignored when using template)</span>}</Label>
                                <Input value={testEmail.subject} onChange={e => setTestEmail({ ...testEmail, subject: e.target.value })} placeholder="Test from A+ CaseFlow" data-testid="test-email-subject" />
                            </div>
                            <div className="md:col-span-2">
                                <Label>Variables (JSON)</Label>
                                <Input value={testEmail.variables} onChange={e => setTestEmail({ ...testEmail, variables: e.target.value })} placeholder='{"first_name":"Alex"}' data-testid="test-email-variables" className="font-mono text-xs" />
                            </div>
                        </div>
                        <Button onClick={sendTestEmail} disabled={testEmailBusy} data-testid="test-email-send">
                            <Send className="h-4 w-4 mr-1" /> {testEmailBusy ? "Sending…" : "Send test email"}
                        </Button>
                        {testEmailResult && (
                            <div className="border border-border rounded-md bg-gray-50 p-3 text-xs font-mono text-gray-800 overflow-auto max-h-64" data-testid="test-email-result">
                                <div className="mb-1 text-[11px] uppercase tracking-wider text-muted-foreground">
                                    {testEmailResult.mocked ? "Result (mocked)" : "Result (LIVE)"}
                                </div>
                                <pre className="whitespace-pre-wrap break-words">{JSON.stringify(testEmailResult, null, 2)}</pre>
                            </div>
                        )}
                    </div>
                </TabsContent>

                <TabsContent value="templates" className="mt-4 space-y-4">
                    <MessageTemplatesPanel />
                </TabsContent>

                <TabsContent value="messaging-routes" className="mt-4 space-y-4">
                    <div className="bg-white border border-border rounded-xl p-6 shadow-sm">
                        <MessagingRoutesPanel />
                    </div>
                </TabsContent>

                <TabsContent value="hours" className="mt-4">
                    <div className="bg-white border border-border rounded-xl p-6 shadow-sm max-w-2xl space-y-4">
                        <div>
                            <h2 className="text-base font-medium text-gray-900">Operating Hours</h2>
                            <p className="text-sm text-muted-foreground mt-0.5">Default send window for campaigns that use "Global Operating Hours".</p>
                        </div>
                        <div>
                            <Label className="mb-2 block">Days</Label>
                            <div className="flex flex-wrap gap-1.5">
                                {[
                                    { v: 0, l: "Mon" }, { v: 1, l: "Tue" }, { v: 2, l: "Wed" },
                                    { v: 3, l: "Thu" }, { v: 4, l: "Fri" }, { v: 5, l: "Sat" }, { v: 6, l: "Sun" },
                                ].map(d => {
                                    const days = s.operating_hours?.days || [0, 1, 2, 3, 4];
                                    const active = days.includes(d.v);
                                    return (
                                        <button
                                            key={d.v}
                                            type="button"
                                            data-testid={`oh-day-${d.v}`}
                                            onClick={() => {
                                                const next = active ? days.filter(x => x !== d.v) : [...days, d.v].sort();
                                                setS({ ...s, operating_hours: { ...(s.operating_hours || {}), days: next } });
                                            }}
                                            className={`px-3 py-1.5 rounded-md text-sm border ${active ? "border-primary bg-primary/10 text-primary" : "border-border bg-white text-gray-700 hover:bg-gray-50"}`}
                                        >
                                            {d.l}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Start</Label>
                                <Input
                                    type="time"
                                    value={s.operating_hours?.start || "09:00"}
                                    onChange={e => setS({ ...s, operating_hours: { ...(s.operating_hours || {}), start: e.target.value } })}
                                    data-testid="oh-start"
                                />
                            </div>
                            <div>
                                <Label>End</Label>
                                <Input
                                    type="time"
                                    value={s.operating_hours?.end || "17:00"}
                                    onChange={e => setS({ ...s, operating_hours: { ...(s.operating_hours || {}), end: e.target.value } })}
                                    data-testid="oh-end"
                                />
                            </div>
                        </div>
                        <div>
                            <Label>Timezone</Label>
                            <Input
                                value={s.operating_hours?.timezone || "America/Los_Angeles"}
                                onChange={e => setS({ ...s, operating_hours: { ...(s.operating_hours || {}), timezone: e.target.value } })}
                                placeholder="e.g. America/Los_Angeles"
                                data-testid="oh-timezone"
                            />
                            <p className="text-xs text-muted-foreground mt-1">IANA timezone identifier.</p>
                        </div>
                        <Button onClick={save} data-testid="save-hours-button">Save</Button>
                    </div>
                </TabsContent>

                <TabsContent value="users" className="mt-4 space-y-4">
                    <div className="bg-white border border-border rounded-xl p-6 shadow-sm">
                        <h2 className="text-base font-medium mb-3">Team</h2>
                        <ul className="divide-y divide-border">
                            {users.map(u => (
                                <li key={u.id} className="py-3 flex items-center gap-3" data-testid={`user-row-${u.id}`}>
                                    <div className="h-8 w-8 rounded-full bg-primary/10 text-primary text-xs font-semibold flex items-center justify-center">{u.name?.[0]}</div>
                                    <div className="flex-1">
                                        <div className="text-sm text-gray-900 font-medium">{u.name}</div>
                                        <div className="text-xs text-muted-foreground">{u.email}</div>
                                    </div>
                                    <span className="text-xs px-2 py-0.5 rounded-md bg-gray-100 text-gray-700 capitalize">{u.role}</span>
                                    {user?.role === "admin" && u.id !== user.id && (
                                        <button onClick={() => removeUser(u.id)} className="p-1.5 text-gray-400 hover:text-rose-600 rounded-md hover:bg-rose-50" data-testid={`delete-user-${u.id}`}>
                                            <Trash2 className="h-4 w-4" />
                                        </button>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </div>

                    {user?.role === "admin" && (
                        <div className="bg-white border border-border rounded-xl p-6 shadow-sm max-w-2xl">
                            <h2 className="text-base font-medium mb-3">Add user</h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div>
                                    <Label>Name</Label>
                                    <Input value={newUser.name} onChange={e => setNewUser({ ...newUser, name: e.target.value })} data-testid="new-user-name" />
                                </div>
                                <div>
                                    <Label>Email</Label>
                                    <Input type="email" value={newUser.email} onChange={e => setNewUser({ ...newUser, email: e.target.value })} data-testid="new-user-email" />
                                </div>
                                <div>
                                    <Label>Role</Label>
                                    <select value={newUser.role} onChange={e => setNewUser({ ...newUser, role: e.target.value })} className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm">
                                        <option value="staff">Staff</option>
                                        <option value="admin">Admin</option>
                                    </select>
                                </div>
                                <div>
                                    <Label>Temp password</Label>
                                    <Input type="password" value={newUser.password} onChange={e => setNewUser({ ...newUser, password: e.target.value })} data-testid="new-user-password" />
                                </div>
                            </div>
                            <Button className="mt-4" onClick={addUser} data-testid="add-user-button">Add user</Button>
                        </div>
                    )}
                </TabsContent>

                <TabsContent value="limits" className="mt-4">
                    <div className="bg-white border border-border rounded-xl p-6 shadow-sm max-w-md">
                        <Label>Max send rate (per hour)</Label>
                        <Input
                            type="number"
                            value={s.max_send_rate_per_hour || 1000}
                            onChange={e => setS({ ...s, max_send_rate_per_hour: e.target.value })}
                            data-testid="max-rate-input"
                        />
                        <Button className="mt-4" onClick={save} data-testid="save-limits-button">Save</Button>
                    </div>
                </TabsContent>
            </Tabs>
        </div>
    );
}
